import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Navigation, Car, Bus, ArrowRight } from 'lucide-react';

const Location = () => {
  const hours = [
    { day: 'Monday', hours: '6:00 AM - 10:00 PM' },
    { day: 'Tuesday', hours: '6:00 AM - 10:00 PM' },
    { day: 'Wednesday', hours: '6:00 AM - 10:00 PM' },
    { day: 'Thursday', hours: '6:00 AM - 10:00 PM' },
    { day: 'Friday', hours: '6:00 AM - 10:00 PM' },
    { day: 'Saturday', hours: '6:00 AM - 10:00 PM' },
    { day: 'Sunday', hours: '6:00 AM - 10:00 PM' },
  ];

  const getCurrentDay = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };

  const isOpenNow = () => {
    const now = new Date();
    const hour = now.getHours();
    // Open 6AM (6) to 10PM (22) every day
    return hour >= 6 && hour < 22;
  };

  return (
    <div className="grain-bg" data-testid="location-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#1A4D2E]" data-testid="location-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-fade-in-up">
              Hours & Location
            </h1>
            <p className="text-xl text-white/80 animate-fade-in-up animation-delay-100">
              Conveniently located in Yettem. Find us easily with our directions and hours below.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24" data-testid="location-content">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Map & Address */}
            <div>
              <div className="map-container mb-6 h-[350px] md:h-[450px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3189.5!2d-119.1689!3d36.4689!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDI4JzA4LjAiTiAxMTnCsDEwJzA4LjAiVw!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Shah Market Location"
                  data-testid="google-map"
                />
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="font-bold text-xl text-[#3D405B] mb-4">Store Address</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F4F1DE] rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin size={24} className="text-[#1A4D2E]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#3D405B]">Shah Market</p>
                      <p className="text-[#5F6C7B]">14346 Avenue 384</p>
                      <p className="text-[#5F6C7B]">Yettem, CA 93292</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#F4F1DE] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone size={24} className="text-[#1A4D2E]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#3D405B]">Phone</p>
                      <a href="tel:5094057493" className="text-[#E07A5F] hover:underline">
                        (509) 405-7493
                      </a>
                    </div>
                  </div>

                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=14346+Avenue+384+Yettem+CA+93292"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2"
                    data-testid="get-directions-btn"
                  >
                    <Navigation size={18} />
                    Get Directions
                  </a>
                </div>
              </div>
            </div>

            {/* Hours & Info */}
            <div>
              {/* Status Card */}
              <div className={`rounded-2xl p-6 mb-6 ${
                isOpenNow() ? 'bg-[#1A4D2E]' : 'bg-[#E07A5F]'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm mb-1">Current Status</p>
                    <p className="text-white font-bold text-2xl">
                      {isOpenNow() ? 'We\'re Open!' : 'Currently Closed'}
                    </p>
                  </div>
                  <div className="open-badge" data-testid="open-status">
                    {isOpenNow() ? 'Open Now' : 'Closed'}
                  </div>
                </div>
              </div>

              {/* Hours Table */}
              <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#F4F1DE] rounded-xl flex items-center justify-center">
                    <Clock size={24} className="text-[#1A4D2E]" />
                  </div>
                  <h3 className="font-bold text-xl text-[#3D405B]">Store Hours</h3>
                </div>

                <div className="hours-list space-y-2" data-testid="hours-table">
                  {hours.map((item, index) => (
                    <div
                      key={index}
                      className={`flex justify-between items-center py-3 px-3 rounded-lg ${
                        item.day === getCurrentDay() ? 'bg-[#F4F1DE]' : 'border-b border-[#F4F1DE]'
                      }`}
                    >
                      <div className={`flex items-center gap-2 ${
                        item.day === getCurrentDay() ? 'text-[#1A4D2E] font-bold' : 'font-medium'
                      }`}>
                        {item.day}
                        {item.day === getCurrentDay() && (
                          <span className="text-xs bg-[#E07A5F] text-white px-2 py-0.5 rounded-full">
                            Today
                          </span>
                        )}
                      </div>
                      <div className={
                        item.day === getCurrentDay() ? 'text-[#1A4D2E] font-semibold' : 'text-[#5F6C7B]'
                      }>
                        {item.hours}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Getting Here */}
              <div className="bg-[#F4F1DE] rounded-2xl p-6">
                <h3 className="font-bold text-xl text-[#3D405B] mb-4">Getting Here</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Car size={20} className="text-[#1A4D2E] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-[#3D405B]">By Car</p>
                      <p className="text-sm text-[#5F6C7B]">
                        Free parking available in front of the store and in the adjacent lot.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Bus size={20} className="text-[#1A4D2E] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-[#3D405B]">By Bus</p>
                      <p className="text-sm text-[#5F6C7B]">
                        Visalia Transit Route 1 & 5 stop directly in front of the store.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#1A4D2E]" data-testid="location-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Questions? Get in Touch!
          </h2>
          <p className="text-white/80 text-lg mb-8">
            We're happy to help with any questions about our store or services.
          </p>
          <Link
            to="/contact"
            className="btn-secondary px-8 py-4 rounded-xl font-semibold inline-flex items-center gap-2 text-lg"
            data-testid="contact-cta-button"
          >
            Contact Us
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Location;
